import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, Loader2, AlertCircle, ArrowLeft, Send, Shield, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: OTP + password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expiresIn, setExpiresIn] = useState(0);
  
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // Countdown timer
  useEffect(() => {
    if (expiresIn > 0) {
      const timer = setInterval(() => {
        setExpiresIn(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [expiresIn]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API}/admin/request-otp`, { email });
      setSuccess(response.data.message);
      setExpiresIn(response.data.expires_in_minutes * 60);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedValue = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedValue.forEach((char, i) => {
        if (i < 6 && /^\d$/.test(char)) {
          newOtp[i] = char;
        }
      });
      setOtp(newOtp);
      const lastFilledIndex = Math.min(pastedValue.length - 1, 5);
      otpRefs[lastFilledIndex]?.current?.focus();
      return;
    }

    if (/^\d$/.test(value) || value === '') {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      // Auto-focus next input
      if (value && index < 5) {
        otpRefs[index + 1]?.current?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/verify-otp`, {
        email,
        code: otpCode,
        password
      });
      localStorage.setItem('admin_token', response.data.access_token);
      navigate('/admin-portal/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      otpRefs[0]?.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setOtp(['', '', '', '', '', '']);

    try {
      const response = await axios.post(`${API}/admin/request-otp`, { email });
      setSuccess('New verification code sent');
      setExpiresIn(response.data.expires_in_minutes * 60);
      otpRefs[0]?.current?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      data-testid="admin-login-page"
      className="min-h-screen bg-background flex items-center justify-center p-6"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=2000&q=80')`
          }}
        />
        <div className="absolute inset-0 bg-mesh opacity-50" />
      </div>
      
      {/* Decorative */}
      <motion.div 
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-glow/8 rounded-full blur-[128px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.08, 0.15, 0.08] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div 
        className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gold/5 rounded-full blur-[100px]"
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 6, repeat: Infinity, delay: 2 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-card rounded-3xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div 
              className="w-16 h-16 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-6"
              animate={{ 
                boxShadow: ['0 0 20px rgba(212,175,55,0.1)', '0 0 40px rgba(212,175,55,0.2)', '0 0 20px rgba(212,175,55,0.1)']
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Shield size={32} strokeWidth={1.5} className="text-gold" />
            </motion.div>
            <h1 className="font-heading text-2xl md:text-3xl text-white mb-2">
              Admin Portal
            </h1>
            <p className="text-white/50 text-sm">
              {step === 1 ? 'Enter your admin email to continue' : 'Enter verification code and password'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.form 
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleEmailSubmit} 
                data-testid="admin-email-form" 
                className="space-y-6"
              >
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-3">
                    Admin Email
                  </label>
                  <div className="input-with-icon">
                    <Mail size={18} className="icon" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      required
                      data-testid="admin-input-email"
                      placeholder="Enter your admin email"
                      autoComplete="email"
                    />
                  </div>
                </div>

                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="admin-login-error"
                    className="flex items-center gap-3 text-red-400 text-sm bg-red-400/10 px-4 py-3 rounded-xl"
                  >
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={isLoading}
                  data-testid="admin-send-otp"
                  className="w-full glass-button px-8 py-4 rounded-xl font-mono text-sm uppercase tracking-widest text-gold flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending Code...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Verification Code
                    </>
                  )}
                </motion.button>
              </motion.form>
            ) : (
              <motion.form 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleVerifySubmit} 
                data-testid="admin-verify-form" 
                className="space-y-6"
              >
                {/* Back Button */}
                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); setError(''); }}
                  className="flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors text-sm mb-4"
                >
                  <ArrowLeft size={16} />
                  Back to email
                </button>

                {/* Success Message */}
                {success && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 text-green-400 text-sm bg-green-400/10 px-4 py-3 rounded-xl"
                  >
                    <CheckCircle size={16} className="flex-shrink-0" />
                    <span>{success}</span>
                  </motion.div>
                )}

                {/* OTP Input */}
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-3">
                    Verification Code
                  </label>
                  <div className="flex justify-between gap-2">
                    {otp.map((digit, index) => (
                      <motion.input
                        key={index}
                        ref={otpRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        data-testid={`otp-input-${index}`}
                        className="otp-input"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      />
                    ))}
                  </div>
                  
                  {/* Timer & Resend */}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-white/30 text-xs font-mono">
                      {expiresIn > 0 ? `Expires in ${formatTime(expiresIn)}` : 'Code expired'}
                    </span>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="text-gold/60 hover:text-gold text-xs font-mono uppercase tracking-widest transition-colors disabled:opacity-50"
                    >
                      Resend Code
                    </button>
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-3">
                    Password
                  </label>
                  <div className="input-with-icon">
                    <Lock size={18} className="icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      required
                      data-testid="admin-input-password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      style={{ paddingRight: '48px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors z-10"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="admin-verify-error"
                    className="flex items-center gap-3 text-red-400 text-sm bg-red-400/10 px-4 py-3 rounded-xl"
                  >
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={isLoading || otp.join('').length !== 6}
                  data-testid="admin-verify-submit"
                  className="w-full glass-button px-8 py-4 rounded-xl font-mono text-sm uppercase tracking-widest text-gold flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Lock size={18} />
                      Verify & Sign In
                    </>
                  )}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Back Link */}
          <div className="mt-8 text-center">
            <a
              href="/"
              className="text-white/30 hover:text-white/60 text-sm transition-colors"
            >
              ← Back to Website
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
